import React, { useState } from "react";
import { CheckCircle2, Loader2, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { submitReadinessMilestone } from "@/lib/api";
import { track, EVENTS } from "@/lib/analytics";
import { errorMessage } from "@/lib/errorMessage";

const COPY = {
  60: {
    tag: "Momentum crossed",
    headline: "You just crossed 60/100.",
    body: "That&apos;s Building Momentum. Drop in an email and we&apos;ll send you a short celebration note plus the next milestone you can aim at.",
  },
  80: {
    tag: "Strong foundation",
    headline: "You just crossed 80/100.",
    body: "That&apos;s a Strong Foundation. Give us your email so we can send you a congratulations plus the maintenance checklist that keeps it there.",
  },
};

export default function MilestoneModal({ milestone, score, kind, name = "", onClose }) {
  const copy = COPY[milestone] || COPY[60];
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await submitReadinessMilestone({ email: email.trim(), milestone, score, kind, name });
      track(EVENTS.EBOOK_WAITLIST_JOIN, { source: `readiness_${milestone}` });
      setDone(true);
      toast.success("You're on the milestone list.");
    } catch (err) {
      toast.error(errorMessage(err, "Could not save your email."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="milestone-modal"
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center px-4"
    >
      <div className="bg-white rounded-2xl max-w-md w-full shadow-xl">
        <div className="flex items-center justify-between p-5 border-b border-[#F0EFEA]">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339]">
            <Sparkles size={12} /> {copy.tag}
          </div>
          <button
            data-testid="milestone-close"
            type="button"
            onClick={onClose}
            className="text-[#5C6B62] hover:text-[#1C2B23]"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {done ? (
            <div className="bg-[#E8F0EB] border border-[#2B5339] rounded-xl p-5 flex items-start gap-3">
              <CheckCircle2 size={18} className="text-[#2B5339] mt-0.5 flex-shrink-0" />
              <div className="text-sm text-[#1C2B23]">
                We&apos;ll be in touch. Keep building your toolboxes.
              </div>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-medium text-[#1C2B23]" data-testid="milestone-headline">
                {copy.headline}
              </h2>
              <p
                className="text-sm text-[#5C6B62] leading-relaxed"
                dangerouslySetInnerHTML={{ __html: copy.body }}
              />
              <form onSubmit={submit} className="space-y-3">
                <input
                  data-testid="milestone-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full bg-[#F9F8F6] border border-[#E5E2DC] rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#2B5339]"
                />
                <div className="flex gap-2">
                  <button
                    data-testid="milestone-submit"
                    type="submit"
                    disabled={submitting || !email}
                    className="flex-1 bg-[#1C2B23] text-white rounded-lg py-2.5 text-sm font-medium hover:bg-[#2B5339] disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  >
                    {submitting && <Loader2 size={13} className="animate-spin" />}
                    Send my milestone note
                  </button>
                  <button
                    data-testid="milestone-skip"
                    type="button"
                    onClick={onClose}
                    className="text-xs text-[#5C6B62] hover:text-[#2B5339] px-3"
                  >
                    Not now
                  </button>
                </div>
              </form>
              <p className="text-[11px] text-[#5C6B62]">
                Just for milestone notes. Unsubscribe with one click, always.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
